// Import React hooks and components for the Generation History component
// This component handles AI generation history display with the following feedback features:
// - Merchants can only submit feedback once per generation
// - Feedback option is hidden for failed generations
// - Once feedback is submitted, a "View Feedback" button is shown instead
// - Feedback view dialog displays the submitted rating, comment, and submission date
import React, { useState, useEffect, useRef } from 'react';
import { formatCredits } from '../../../utils/numberFormat';
import Cookies from 'js-cookie';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  LinearProgress,
  Alert,
  Tooltip,
  CircularProgress,
  Rating
} from '@mui/material';
import { Skeleton } from '@mui/material';
import {
  Search,
  FilterList,
  Download,
  Preview,
  Refresh,
  Delete,
  Star,
  StarBorder,
  CheckCircle,
  Schedule,
  Error,
  RateReview,
  PlayArrow,
  Cancel,
  ZoomIn,
  Close,
  Image as ImageIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { aiGenerationAPI } from '../../../services/merchantAPI';
import { useMerchantAuth } from '../../../contexts/MerchantAuthContext';
import DynamicMerchantTabs from '../Layout/DynamicMerchantTabs';
import { useMerchantPermissions } from '../../../contexts/MerchantPermissionContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_URLS } from '../../../config/apiConfig';
import { AutoCloseAlert } from '../../Common';
import FlutterServicesAPI from '../../../services/flutterServices';
import { getSecureVideoStreamUrl, getThumbnailUrl as getSecureThumbnailUrl } from '../../../utils/imageUrlUtils';
import {
  getGenerationDownloadBaseName,
  getGenerationDownloadFileName
} from '../../../utils/generationDownloadFilename';
import SecureImage from '../../Common/SecureImage';
import SecureVideo from '../../Common/SecureVideo';
import ShareSocialButton from '../../Common/ShareSocialButton';
import PinchZoomPanContainer from '../../Common/PinchZoomPanContainer';
import useRouteMeta from '../../../hooks/useRouteMeta';
import GenerationPreviewDialog from './GenerationPreviewDialog';

// Inline tag editor styled components (mirrors GenerationInfoPopup.js)
const HistTagsContainer = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px',
  padding: '6px 12px',
  border: '1px solid #E5E7EB',
  borderRadius: '8px',
  backgroundColor: 'white',
  minHeight: '40px',
  alignItems: 'center',
  boxSizing: 'border-box',
  '&:focus-within': {
    borderColor: '#6318C6',
    boxShadow: '0 0 0 1px #6318C6'
  }
}));

const HistTagInput = styled('input')({
  border: 'none',
  outline: 'none',
  fontSize: '13px',
  fontWeight: '500',
  color: 'black',
  flex: 1,
  minWidth: '100px',
  background: 'transparent',
  padding: '0',
  fontFamily: 'var(--font-montserrat-medium)',
  '&::placeholder': { color: '#9CA3AF', fontWeight: '500' }
});

const HistTagChip = styled(Chip)(() => ({
  backgroundColor: 'white',
  border: '1px solid #000000',
  color: '#000000',
  fontWeight: '500',
  height: '26px',
  borderRadius: '13px',
  fontSize: '12px',
  fontFamily: 'var(--font-montserrat-medium)',
  '& .MuiChip-label': { paddingLeft: '10px', paddingRight: '6px' },
  '& .MuiChip-deleteIcon': { color: '#000000', fontSize: '15px', marginRight: '4px' }
}));

// Infinite scroll / API page size (skeleton placeholders use the same count).
const HISTORY_ITEMS_PER_PAGE = 6;

// Main component for displaying AI generation history with filtering, search, and management capabilities
const GenerationHistory = () => {
  useRouteMeta();
  const [generations, setGenerations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // State to store total count from API response (not current page count)
  const [totalGenerations, setTotalGenerations] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTags, setSearchTags] = useState([]); // multi-tag search
  const [statusFilter, setStatusFilter] = useState('');
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateError, setDateError] = useState('');

  // Temporary states for filters that are only applied on Apply button click
  const [tempStatusFilter, setTempStatusFilter] = useState('');
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');
  const [tempSearchTerm, setTempSearchTerm] = useState('');

  // Tag search / autocomplete state for main search bar
  const [tagInput, setTagInput] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [tagSuggestionsLoading, setTagSuggestionsLoading] = useState(false);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const tagSuggestCacheRef = useRef(new Map());
  const tagSuggestAbortRef = useRef(null);
  const tagInputRef = useRef(null);
  const tagDropdownRef = useRef(null);
  const [selectedGeneration, setSelectedGeneration] = useState(null);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [error, setError] = useState('');
  const [feedbackDialog, setFeedbackDialog] = useState(false);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  const [feedbackError, setFeedbackError] = useState(''); // Separate error state for feedback validation
  const [zoomDialog, setZoomDialog] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  // State to track feedback submission loading state
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  // State for feedback view dialog
  const [feedbackViewDialog, setFeedbackViewDialog] = useState(false);
  // State for filter dropdown panel toggle
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  // State for custom tags expand per card
  const [expandedTags, setExpandedTags] = useState({});
  // Inline tag editing state
  const [editingTagsId, setEditingTagsId] = useState(null); // generation.id being edited
  const [tagEditState, setTagEditState] = useState({ tags: [], input: '', saving: false, error: '' });
  // Infinite scroll state
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [showNextSkeleton, setShowNextSkeleton] = useState(false);
  const [customTagSuggestions, setCustomTagSuggestions] = useState([]);
  const [isTagSuggestionsLoading, setIsTagSuggestionsLoading] = useState(false);
  const [isTagInputFocused, setIsTagInputFocused] = useState(false);
  const minTagSuggestChars = 2;
  const latestTagSuggestionRequestRef = useRef(0);
  const customTagSuggestCacheRef = useRef(new Map());
  const customTagSuggestAbortRef = useRef(null);

  const sentinelRef = useRef(null);
  const filterPanelRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        filterPanelOpen &&
        filterPanelRef.current &&
        !filterPanelRef.current.contains(event.target) &&
        !event.target.closest('.MuiPickersPopper-root') &&
        !event.target.closest('.MuiPopover-root') &&
        !event.target.closest('.MuiModal-root') &&
        !event.target.closest('.MuiDialog-root')
      ) {
        setFilterPanelOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [filterPanelOpen]);
  const { hasPermission, loading: permissionLoading } = useMerchantPermissions();
  const canSubmitFeedback = hasPermission('Generation History', 'feedback');
  const canDownload = hasPermission('Generation History', 'download');
  const canDelete = hasPermission('Generation History', 'delete');
  const canPreview = hasPermission('Generation History', 'preview');
  const canRegenerate = hasPermission('Generation History', 'regenerate');

  // List-row thumbnails only: browser hints via SecureImage → <img> (dialogs/zoom keep default priority)
  const HISTORY_LIST_THUMB_IMG_HINTS = { loading: 'lazy', decoding: 'async' };

  const { merchant, refreshProfile } = useMerchantAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Date validation function
  const validateDates = (start, end) => {
    const today = new Date();
    // Use local timezone date instead of UTC
    const todayStr = today.getFullYear() + '-' +
      String(today.getMonth() + 1).padStart(2, '0') + '-' +
      String(today.getDate()).padStart(2, '0');

    // Only validate if dates are provided
    if (!start && !end) return '';

    if (start && start > todayStr) {
      return "Start date cannot be in the future";
    }
    if (end && end > todayStr) {
      return "End date cannot be in the future";
    }
    if (start && end && start > end) {
      return "Start date cannot be after end date";
    }
    return '';
  };

  // Function to sync temporary states with actual filter states
  const syncTempFilters = () => {
    setTempStatusFilter(statusFilter);
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setTempSearchTerm(searchTerm);
  };

  // Function to apply filters from temporary states
  const applyFilters = () => {
    // Validate dates before applying
    const error = (tempStartDate || tempEndDate) ? validateDates(tempStartDate, tempEndDate) : '';
    setDateError(error);

    if (error) {
      return; // Don't apply filters if validation fails
    }

    // Apply temporary values to actual filter states
    setStatusFilter(tempStatusFilter);
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setSearchTerm(tempSearchTerm);

    // Close filter panel
    setFilterPanelOpen(false);
  };

  // Close tag dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        tagDropdownRef.current && !tagDropdownRef.current.contains(e.target) &&
        tagInputRef.current && !tagInputRef.current.contains(e.target)
      ) {
        setShowTagDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch tag suggestions as user types
  useEffect(() => {
    const query = tagInput.trim();
    if (query.length < 1) {
      setTagSuggestions([]);
      setShowTagDropdown(false);
      return;
    }
    const normalizedQuery = query.toLowerCase();
    const cached = tagSuggestCacheRef.current.get(normalizedQuery);
    if (cached) {
      const filtered = cached.filter(t => !searchTags.includes(t));
      setTagSuggestions(filtered);
      setShowTagDropdown(filtered.length > 0);
      return;
    }
    const debounceId = setTimeout(async () => {
      try {
        setTagSuggestionsLoading(true);
        if (tagSuggestAbortRef.current) tagSuggestAbortRef.current.abort();
        const controller = new AbortController();
        tagSuggestAbortRef.current = controller;
        const { data } = await aiGenerationAPI.getSearchSuggestions(
          { q: query, limit: 12 },
          { signal: controller.signal }
        );
        const fetched = Array.isArray(data?.suggestions) ? data.suggestions : [];
        tagSuggestCacheRef.current.set(normalizedQuery, fetched);
        const filtered = fetched.filter(t => !searchTags.includes(t));
        setTagSuggestions(filtered);
        setShowTagDropdown(filtered.length > 0);
      } catch (err) {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        setTagSuggestions([]);
      } finally {
        setTagSuggestionsLoading(false);
      }
    }, 200);
    return () => {
      clearTimeout(debounceId);
      if (tagSuggestAbortRef.current) { tagSuggestAbortRef.current.abort(); tagSuggestAbortRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagInput, searchTags]);

  // Helpers for tag search
  const addTagToSearch = (tagSuggestion) => {
    const tag = typeof tagSuggestion === 'string' ? tagSuggestion : tagSuggestion.label;
    const trimmed = tag.trim();
    if (!trimmed || searchTags.includes(trimmed)) return;
    const newTags = [...searchTags, trimmed];
    setSearchTags(newTags);
    setSearchTerm(newTags.join(','));
    setTagInput('');
    setTagSuggestions([]);
    setShowTagDropdown(false);
  };

  const removeTagFromSearch = (tag) => {
    const newTags = searchTags.filter(t => t !== tag);
    setSearchTags(newTags);
    setSearchTerm(newTags.join(','));
  };

  // Reset page to 1 when statusFilter, searchTerm, or date filters change to ensure proper pagination
  useEffect(() => {
    setPage(1);
  }, [statusFilter, searchTerm, startDate, endDate]);

  // Initial and filter/search-driven load (resets to first page)
  useEffect(() => {
    setPage(1);
    setHasMore(true);
    loadGenerations({ append: false, targetPage: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, searchTerm, startDate, endDate]);

  // Load when page changes due to infinite scroll (append subsequent pages)
  useEffect(() => {
    if (page === 1) return;
    loadGenerations({ append: true, targetPage: page });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);


  // Handle video autoplay when play button is clicked in zoom dialog
  useEffect(() => {
    if (isVideoPlaying && zoomDialog && zoomedImage && zoomedImage.type === 'video') {
      // Small delay to ensure video element is rendered by SecureVideo
      const timer = setTimeout(() => {
        // Find all video elements and try to play the one in the zoom dialog
        // The zoom dialog is the last dialog (most recent) on the page
        const allDialogs = document.querySelectorAll('[role="dialog"]');
        if (allDialogs.length > 0) {
          // Get the last dialog (should be the zoom dialog if it's open)
          const zoomDialogElement = allDialogs[allDialogs.length - 1];
          const videoElement = zoomDialogElement.querySelector('video');
          if (videoElement) {
            // Try to play the video
            videoElement.play().catch(err => {
              // If autoplay fails (e.g., browser policy), user can still click play button
            });
          }
        }
      }, 300); // Increased delay to ensure SecureVideo has rendered the video element

      return () => clearTimeout(timer);
    }
  }, [isVideoPlaying, zoomDialog, zoomedImage]);

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const response = await aiGenerationAPI.getOptions();
        setAvailableStatuses(response.data.options.generation_statuses || []);
      } catch (error) {
      }
    };
    fetchStatuses();
  }, []);

  // Fetch custom-tag suggestions triggered by inline editor input
  useEffect(() => {
    const query = tagEditState.input.trim();
    if (!isTagInputFocused || query.length < minTagSuggestChars || !editingTagsId) {
      setCustomTagSuggestions([]);
      setIsTagSuggestionsLoading(false);
      if (customTagSuggestAbortRef.current) {
        customTagSuggestAbortRef.current.abort();
        customTagSuggestAbortRef.current = null;
      }
      return undefined;
    }

    const normalizedQuery = query.toLowerCase();
    const cachedSuggestions = customTagSuggestCacheRef.current.get(normalizedQuery);
    if (cachedSuggestions) {
      const existingTagSet = new Set(tagEditState.tags.map((tag) => String(tag).trim().toLowerCase()));
      setCustomTagSuggestions(
        cachedSuggestions.filter((tag) => !existingTagSet.has(String(tag).trim().toLowerCase()))
      );
      setIsTagSuggestionsLoading(false);
      return undefined;
    }

    const requestId = latestTagSuggestionRequestRef.current + 1;
    latestTagSuggestionRequestRef.current = requestId;

    const debounceId = setTimeout(async () => {
      try {
        setIsTagSuggestionsLoading(true);
        if (customTagSuggestAbortRef.current) {
          customTagSuggestAbortRef.current.abort();
        }
        const abortController = new AbortController();
        customTagSuggestAbortRef.current = abortController;
        const { data } = await aiGenerationAPI.getSearchSuggestions(
          { q: query, limit: 8 },
          { signal: abortController.signal }
        );
        if (requestId !== latestTagSuggestionRequestRef.current) return;
        const fetchedSuggestions = Array.isArray(data?.suggestions) ? data.suggestions : [];
        customTagSuggestCacheRef.current.set(normalizedQuery, fetchedSuggestions);
        const existingTagSet = new Set(tagEditState.tags.map((tag) => String(tag).trim().toLowerCase()));
        const filteredSuggestions = fetchedSuggestions.filter((tag) => {
          const normalizedTag = String(tag).trim().toLowerCase();
          return normalizedTag && !existingTagSet.has(normalizedTag);
        });
        setCustomTagSuggestions(filteredSuggestions);
      } catch (error) {
        if (error?.name === 'CanceledError' || error?.name === 'AbortError') return;
        if (requestId !== latestTagSuggestionRequestRef.current) return;
        setCustomTagSuggestions([]);
      } finally {
        if (requestId === latestTagSuggestionRequestRef.current) {
          setIsTagSuggestionsLoading(false);
        }
      }
    }, 250);

    return () => {
      clearTimeout(debounceId);
      if (customTagSuggestAbortRef.current) {
        customTagSuggestAbortRef.current.abort();
        customTagSuggestAbortRef.current = null;
      }
    };
  }, [tagEditState.input, tagEditState.tags, isTagInputFocused, editingTagsId, minTagSuggestChars]);


  const isImageLoadedRef = useRef(false);


  // Check for stale processing generations on component mount
  // This automatically marks any processing generations older than 2 hours as failed
  useEffect(() => {
    const checkStaleProcessing = async () => {
      try {
        const response = await aiGenerationAPI.checkStaleProcessing();

        if (response.data.success && response.data.updated_count > 0) {
          // Show success message to user
          // setSuccessMessage(`${response.data.updated_count} stale processing generation${response.data.updated_count > 1 ? 's' : ''} marked as failed`);
          // Refresh the generation list to show updated statuses
          loadGenerations();
        } else {
        }
      } catch (error) {
      }
    };

    checkStaleProcessing();
  }, []); // Run only once on component mount

  // Load generation history data with pagination, filtering, and search capabilities
  const loadGenerations = async (opts = { append: false, targetPage: null }) => {
    const perPage = HISTORY_ITEMS_PER_PAGE;
    const pageToLoad = (opts.targetPage !== null && opts.targetPage !== undefined) ? opts.targetPage : page;
    if (opts.append) {
      setIsFetchingMore(true);
    } else {
      setLoading(true);
    }
    setError('');
    try {
      const params = {
        page: pageToLoad,
        per_page: perPage,
      };
      if (statusFilter && statusFilter !== 'PENDING_OR_PROCESSING') {
        params.status_name = statusFilter;
      } else if (statusFilter === 'PENDING_OR_PROCESSING') {
        params.status_name = statusFilter;
      }
      if (searchTerm) {
        params.tag = searchTerm;
      }
      if (startDate) {
        params.start_date = startDate;
      }
      if (endDate) {
        params.end_date = endDate;
      }


      const response = await aiGenerationAPI.getHistory(params);
      let gens = response.data.generations;

      // Store total count from API response (total_generations field)
      // This represents the total count across all pages, not just current page
      const total = response.data.total_generations || 0;
      setTotalGenerations(total);

      if (statusFilter === 'PENDING_OR_PROCESSING') {
        gens = gens.filter(
          g => g.status?.name === 'PENDING' || g.status?.name === 'PROCESSING'
        );
        // Use backend total_pages only to cap pagination; do not inflate pages based on current-page filtered count
        setTotalPages(response.data.total_pages);
      } else {
        setTotalPages(response.data.total_pages);
      }
      // Determine if there are more pages using backend total_pages
      let more = pageToLoad < (response.data.total_pages || 1);
      // If filtering for processing and first page has zero results, stop immediately
      if (statusFilter === 'PENDING_OR_PROCESSING' && pageToLoad === 1 && gens.length === 0) {
        more = false;
      }
      setHasMore(more);

      if (opts.append) {
        setGenerations(prev => {
          const prevIds = new Set(prev.map(g => g.id));
          const merged = [...prev];
          for (const g of gens) {
            if (!prevIds.has(g.id)) merged.push(g);
          }
          return merged;
        });
      } else {
        setGenerations(gens);
      }
    } catch (error) {
      setError('Failed to load generation history');
    } finally {
      if (opts.append) {
        setIsFetchingMore(false);
        setShowNextSkeleton(false);
      } else {
        setLoading(false);
      }
    }
  };

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first.isIntersecting && hasMore && !isFetchingMore && !loading && !showNextSkeleton) {
        // Next tick only — was 2000ms and blocked the next API call on slow networks
        setShowNextSkeleton(true);
        setTimeout(() => {
          setPage((prev) => prev + 1);
        }, 0);
      }
    }, { root: null, rootMargin: '200px', threshold: 0.0 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, isFetchingMore, loading, showNextSkeleton]);

  // Handle scroll-to-top without trimming already loaded generations
  // This ensures previously loaded pages remain in memory when the user scrolls up and down
  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY <= 0) {
        // Just hide the skeleton loader when user reaches the top
        setShowNextSkeleton(false);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      setPage(1);              // reset page
      setGenerations([]);      // clear old data
      setHasMore(true);        // reset pagination state
      // Refresh both generation history and merchant profile
      await Promise.all([
        loadGenerations({ append: false, targetPage: 1 }),
        refreshProfile()
      ]);
      setSuccessMessage('Data refreshed successfully!');
    } catch (error) {
      setError('Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle download for both mobile and web platforms
   * @param {Object} generation - Generation object to download
   */
  const handleDownload = async (generation) => {
    try {
      if (!canDownload) {
        setError('You do not have permission to download generations');
        return;
      }

      // Check if running in Flutter app (mobile)
      if (FlutterServicesAPI.isFlutterApp()) {
        // Get access token for authentication
        const accessToken = Cookies.get('merchant_access_token');
        if (!accessToken) {
          setError('Authentication required for download');
          setSuccessMessage('');
          return;
        }

        // Always use the POST endpoint for downloads to ensure proper authentication
        const downloadUrl = `${API_URLS.BACKEND_BASE}/api/merchant/ai-generation/download/${generation.id}`;

        const isReelGen = generation.jewelry_category?.display_name === 'Reel';
        const isVideoGen = generation.output_format?.name === 'VIDEO' || isReelGen;
        const downloadData = {
          generationId: generation.id,
          fileType: isVideoGen ? 'VIDEO' : 'IMAGE',
          fileName: getGenerationDownloadBaseName(generation),
          downloadUrl: downloadUrl,
          fileExtension: isVideoGen ? 'mp4' : getFileExtension(generation.output_format),
          method: 'POST', // Always use POST for the download endpoint
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          accessToken: accessToken // Pass token separately for Flutter bridge
        };

        // Call mobile download handler with permission check and Flutter bridge
        try {
          const mobileResult = await FlutterServicesAPI.downloadFileToStorage(downloadData);

          if (mobileResult && mobileResult.success) {
            // setSuccessMessage('File downloaded to device storage successfully!');
            setError('');
            // Refresh the list to update download count
            // loadGenerations();
          } else {
            setError(mobileResult?.message || 'Mobile download failed');
            setSuccessMessage('');
          }
        } catch (mobileError) {
          // Don't show error if download might have succeeded
          // Many Flutter bridges don't return proper responses but still download successfully
          // This is a common pattern where the bridge initiates the download but doesn't return a response
          // setSuccessMessage('File download initiated! Check your device storage.');
          setError('');
          // Still refresh to update download count
          // loadGenerations();
        }
      } else {
        // Web download - existing logic (unchanged)
        const response = await aiGenerationAPI.downloadGeneration(generation.id);
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        const isReelDownload = generation.jewelry_category?.display_name === 'Reel'
          || generation.output_format?.name === 'VIDEO';
        a.download = getGenerationDownloadFileName(
          generation,
          isReelDownload ? 'mp4' : getFileExtension(generation.output_format)
        );
        document.body.appendChild(a);
        a.click();
        // Delay cleanup to ensure mobile browsers complete download
        setTimeout(() => {
          a.remove();
          window.URL.revokeObjectURL(url);
        }, 5000);

        // Refresh the list to update download count
        loadGenerations();
      }
    } catch (error) {
      if (error?.response?.status === 429) return;
      setError('Failed to download file');
      setSuccessMessage('');
    }
  };

  const handlePreview = async (generation) => {
    try {
      await aiGenerationAPI.checkPreviewLimit(generation.id);
    } catch (err) {
      if (err?.response?.status === 429) return;
    }
    setSelectedGeneration(generation);
    setPreviewDialog(true);
  };

  const handleFeedback = () => {
    // Prevent feedback for refunded generations
    if (selectedGeneration && selectedGeneration.is_refunded) {
      return;
    }

    setFeedbackDialog(true);
    setFeedbackRating(0);
    setFeedbackComment('');
    setError(''); // Clear any previous errors when opening feedback dialog
    setSuccessMessage(''); // Clear any previous success messages when opening feedback dialog
    setFeedbackError(''); // Clear any previous feedback validation errors when opening feedback dialog
  };

  // Handle viewing existing feedback
  const handleViewFeedback = (generation) => {
    setSelectedGeneration(generation);
    setFeedbackViewDialog(true);
  };

  // Handle regenerating a generation - navigate to AI generation page with pre-filled data
  const handleRegenerate = (generation) => {
    try {
      // Prepare the data to pre-fill the AI generation form (excluding image)
      const prefillData = {
        // Don't include original_image_path - user needs to upload new image
        // Try different possible field names and structures
        category_id: generation.category_id || generation.category?.id || generation.jewelry_category?.id,
        material_id: generation.material_id || generation.material?.id || generation.jewelry_material?.id,
        model_type_id: generation.model_type_id || generation.model_type?.id || generation.bust_type?.id,
        bust_color_id: generation.bust_color_id || generation.bust_color?.id,
        bust_finish_id: generation.bust_finish_id || generation.bust_finish?.id,
        ethnicity_id: generation.ethnicity_id || generation.ethnicity?.id || generation.gender_option_id || generation.gender_option?.id,
        skin_tone_id: generation.skin_tone_id || generation.skin_tone?.id,
        clothing_type_id: generation.clothing_type_id || generation.clothing_type?.id,
        background_type_id: generation.background_type_id || generation.background_type?.id,
        media_quality: generation.media_quality?.name || generation.media_quality?.display_name || (typeof generation.media_quality === 'string' ? generation.media_quality : ''),
        output_format_id: generation.output_format_id || generation.output_format?.id,
        watermark_type: generation.watermark_type,
        has_watermark: generation.has_watermark,
        has_brand_logo: generation.has_brand_logo || (generation.brand_logo_id ? true : false),
        brand_logo_id: generation.brand_logo_id,
        model_face_image_id: generation.model_face_image_id || generation.selected_ai_model?.id || (typeof generation.selected_ai_model === 'string' ? generation.selected_ai_model : ''),
        brand_watermark: generation.brand_watermark,
        use_model_lighting: generation.use_model_lighting,
        use_model_skin_tone: generation.use_model_skin_tone,
        custom_prompt: generation.custom_prompt,
        // Align with Gallery regenerate + AIGenerationWizard URL prefill (caption fields).
        caption_text_1: generation.caption_text_1 || '',
        caption_text_2: generation.caption_text_2 || ''
      };

      // Convert to URL search parameters
      const searchParams = new URLSearchParams();

      // Add a flag to indicate this is a regeneration
      searchParams.append('regenerate', 'true');
      searchParams.append('from_generation_id', generation.id);

      // Add all prefillData fields to URL parameters (only if not null/undefined)
      Object.entries(prefillData).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          searchParams.append(key, value);
        }
      });

      // Navigate to AI generation page with pre-filled data
      navigate(`/generate?${searchParams.toString()}`);
    } catch (error) {
      setError('Failed to navigate to generation page. Please try again.');
    }
  };

  const handleZoomImage = (imageSrc, imageType, generationId = null) => {
    setZoomedImage({ src: imageSrc, type: imageType, generationId });
    setZoomDialog(true);
    setIsVideoPlaying(false); // Reset video playing state when opening zoom dialog
  };

  // Handle play media in zoom
  const handlePlayMedia = (mediaSrc, mediaType, generationId = null) => {
    setZoomedImage({ src: mediaSrc, type: mediaType, generationId });
    setZoomDialog(true);
  };

  // Get preview URL for generation - Now uses secure URLs with JWT tokens
  const getPreviewUrl = (generation) => {
    if (generation.output_format?.name === 'VIDEO') {
      return getSecureVideoStreamUrl(generation.id);
    } else {
      return getSecureThumbnailUrl(generation.id, '1200x1200');
    }
  };

  // Get file type
  const getFileType = (generation) => {
    return generation.output_format?.name === 'VIDEO' ? 'video' : 'image';
  };

  // Helper function to check if feedback exists for a generation
  const hasFeedback = (generation) => {
    return generation.feedback_rating !== null && generation.feedback_rating !== undefined;
  };

  // Helper function to check if generation can have feedback (not failed)
  const canHaveFeedback = (generation) => {
    return generation.status?.name !== 'FAILED';
  };

  // Helper function to check if feedback button should be disabled
  const isFeedbackDisabled = (generation) => {
    return generation.status?.name === 'PENDING' ||
      generation.status?.name === 'PROCESSING' ||
      generation.is_refunded;
  };

  // Helper function to check if generation can be regenerated
  const canRegenerateGeneration = (generation) => {
    // Can regenerate completed, failed, or processing generations
    return generation.status?.name === 'COMPLETED' ||
      generation.status?.name === 'FAILED' ||
      generation.status?.name === 'PROCESSING';
  };

  // Handle feedback submission with loading state and error handling
  const handleSubmitFeedback = async () => {
    if (!selectedGeneration) {
      setFeedbackError('No generation selected');
      return;
    }

    // Prevent feedback submission for refunded generations
    if (selectedGeneration.is_refunded) {
      setFeedbackError('Feedback not available for refunded generations');
      return;
    }

    // Comment is now optional, so no validation needed
    // Rating is no longer required

    // Set loading state
    setFeedbackSubmitting(true);
    setError('');

    try {
      // Send feedback with optional rating and comment
      const feedbackData = {
        rating: feedbackRating ? feedbackRating : 5, // Only send rating if provided
        comment: feedbackComment.trim() || null, // Only send comment if provided
      };

      await aiGenerationAPI.submitFeedback(selectedGeneration.id, feedbackData);

      // Show success message with feedback details
      let feedbackDetails = [];
      // if (feedbackRating > 0) {
      //   feedbackDetails.push(`Rating: ${feedbackRating} star${feedbackRating > 1 ? 's' : ''}`);
      // }
      if (feedbackComment.trim()) {
        feedbackDetails.push(`Comment: "${feedbackComment.trim()}"`);
      }

      const detailsText = feedbackDetails.length > 0 ? ` (${feedbackDetails.join(', ')})` : '';
      setSuccessMessage(`Feedback submitted successfully!${detailsText}`);

      setError('');
      setFeedbackError(''); // Clear feedback validation error
      // Optimistically update the selected generation in the existing list
      // so that we don't drop already loaded pages by reloading a single page.
      setGenerations(prev =>
        prev.map(g =>
          g.id === selectedGeneration.id
            ? {
              ...g,
              feedback_rating: feedbackRating ? feedbackRating : 5,
              feedback_comment: feedbackComment.trim() || null,
              feedback_submitted_at: new Date().toISOString(),
            }
            : g
        )
      );
      setFeedbackDialog(false); // Close popup after successful submission

      // Reset feedback form
      setFeedbackRating(0);
      setFeedbackComment('');
    } catch (error) {
      // Provide more specific error message based on the response
      if (error.response?.status === 400) {
        setError('Invalid feedback data. Please check your comment and try again.');
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Failed to submit feedback. Please try again.');
      }
      setSuccessMessage('');
    } finally {
      // Always reset loading state
      setFeedbackSubmitting(false);
    }
  };

  const getFileExtension = (outputFormat) => {
    if (!outputFormat) return 'jpg';

    // Handle both string format and object with name property
    const formatValue = typeof outputFormat === 'string' ? outputFormat : outputFormat?.name;

    if (!formatValue || typeof formatValue !== 'string') {
      return 'jpg';
    }

    switch (formatValue.toUpperCase()) {
      case 'VIDEO': return 'mp4';
      case 'GIF': return 'gif';
      default: return 'jpg';
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'default';

    // Handle both string status and object with name property
    const statusValue = typeof status === 'string' ? status : status?.name;

    if (!statusValue || typeof statusValue !== 'string') {
      return 'default';
    }

    switch (statusValue.toUpperCase()) {
      case 'COMPLETED': return 'success';
      case 'PROCESSING': return 'warning';
      case 'PENDING': return 'info';
      case 'FAILED': return 'error';
      case 'CANCELLED': return 'default';
      default: return 'default';
    }
  };

  const formatBatchId = (batchId) => {
    if (!batchId) return null;

    // If batch ID is short (less than 16 characters), show it as is
    if (batchId.length <= 16) {
      return batchId;
    }

    // For longer batch IDs, show first 8 characters with ellipsis
    const firstPart = batchId.substring(0, 8);
    return `${firstPart}...`;
  };

  /** Parse custom_tags from a generation object into a string array */
  const parseHistoryTags = (generation) => {
    const raw = generation.custom_tags;
    if (Array.isArray(raw)) return raw.map(t => String(t).trim()).filter(Boolean);
    if (raw != null && raw !== '') {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : String(raw).split(',').map(t => t.trim()).filter(Boolean);
      } catch (e) {
        return String(raw).split(',').map(t => t.trim()).filter(Boolean);
      }
    }
    return [];
  };

  /** Open inline tag editor for a card */
  const handleStartTagEdit = (generation) => {
    setEditingTagsId(generation.id);
    setTagEditState({ tags: parseHistoryTags(generation), input: '', saving: false, error: '' });
  };

  /** Save tags for the currently-edited card */
  const handleSaveHistoryTags = async (generationId) => {
    setTagEditState(prev => ({ ...prev, saving: true, error: '' }));
    const draft = tagEditState.input.trim();
    const draftTags = draft ? draft.split(',').map(t => t.trim()).filter(Boolean) : [];
    const existing = new Set(tagEditState.tags.map(t => t.toLowerCase()));
    const finalTags = [...tagEditState.tags, ...draftTags.filter(t => !existing.has(t.toLowerCase()))];
    try {
      const { data } = await aiGenerationAPI.patchGenerationCustomTags(generationId, { custom_tags: finalTags });
      const saved = data?.custom_tags ?? finalTags.join(',');
      setGenerations(prev =>
        prev.map(g =>
          g.id === generationId ? { ...g, custom_tags: saved } : g
        )
      );
      setEditingTagsId(null);
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Failed to save tags';
      setTagEditState(prev => ({ ...prev, saving: false, error: msg }));
    }
  };

  /** Handle keydown inside inline tag input */
  const handleHistTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab' || e.key === ' ') {
      if (tagEditState.input.trim()) {
        e.preventDefault();
        const newTags = tagEditState.input.split(',').map(t => t.trim()).filter(Boolean);
        setTagEditState(prev => {
          const existing = new Set(prev.tags.map(t => t.toLowerCase()));
          const unique = newTags.filter(t => !existing.has(t.toLowerCase()));
          return { ...prev, tags: [...prev.tags, ...unique], input: '' };
        });
        setCustomTagSuggestions([]);
      } else if (e.key === ',') {
        e.preventDefault();
      }
    } else if (e.key === 'Backspace' && !tagEditState.input) {
      setTagEditState(prev => ({ ...prev, tags: prev.tags.slice(0, -1) }));
    } else if (e.key === 'Escape') {
      if (customTagSuggestions.length > 0) {
        setCustomTagSuggestions([]);
      } else {
        setEditingTagsId(null);
      }
    }
  };

  /** Handle suggestion selection */
  const handleTagSuggestionSelect = (tagSuggestion) => {
    const tag = typeof tagSuggestion === 'string' ? tagSuggestion : tagSuggestion.label;
    const trimmedValue = tag.trim();
    setTagEditState(prev => {
      const existing = new Set(prev.tags.map(t => t.toLowerCase()));
      if (!existing.has(trimmedValue.toLowerCase())) {
        return { ...prev, tags: [...prev.tags, trimmedValue], input: '' };
      }
      return { ...prev, input: '' };
    });
    setCustomTagSuggestions([]);
  };

  return (
    <>
      <DynamicMerchantTabs />
      <Container maxWidth={false} disableGutters sx={{ mb: 0, px: 0, mx: 0 }}>
        <Paper elevation={0} sx={{
          p: { xs: 0, md: 3 },
          px: { xs: 0, md: 3 },
          mx: 0,
          border: { xs: 'none', md: '1px solid #e5e7eb' },
          boxShadow: 'none',
          backgroundColor: { xs: 'transparent', md: 'white' }
        }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography
              variant="h6"
              fontWeight={700}
              fontSize={{ xs: '18px', sm: '22px', md: '28px' }}
              color="#1E2939"
              fontFamily="var(--font-montserrat-semibold)"
            >
              History
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={loading}
              sx={{
                borderRadius: 2,
                borderColor: '#e5e7eb',
                color: '#1e293b',
                fontWeight: 600,
                height: '44px',
                minHeight: '44px',
                textTransform: 'none',
                px: { xs: 1.5, md: 2 },
                py: 1,
                fontSize: 15,
                backgroundColor: '#f3f0ff',
                minWidth: { xs: 'auto', md: 'auto' },
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 1,
                '&:hover': {
                  backgroundColor: '#f3f0ff',
                  borderColor: '#a78bfa',
                },
                '& .MuiButton-startIcon': {
                  marginRight: 0,
                  marginLeft: 0,
                },
              }}
            >
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>Refresh</Box>
            </Button>
          </Box>

          {error && !feedbackDialog && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert
              severity="success"
              sx={{ mb: 3 }}
              onClose={() => setSuccessMessage('')}
            >
              {successMessage}
            </Alert>
          )}

          {/* Search + Filter Toggle UI */}
          <Box sx={{ mb: 2, position: 'relative', zIndex: 15 }}>
            {/* Search bar row with filter button */}
            <Box display="flex" alignItems="center" gap={1}>
              <Box sx={{ position: 'relative', flex: 1, maxWidth: { md: '550px' } }}>
                <Box
                  onClick={() => tagInputRef.current && tagInputRef.current.focus()}
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '6px',
                    minHeight: '44px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '10px',
                    backgroundColor: '#fff',
                    padding: '5px 12px 5px 10px',
                    cursor: 'text',
                    boxSizing: 'border-box',
                    '&:focus-within': { borderColor: '#6318C6', boxShadow: '0 0 0 1px #6318C620' },
                  }}
                >
                  {/* Search icon */}
                  <Box
                    component="img"
                    src="/assets/img/icons/search-icon.svg"
                    alt="search"
                    sx={{ width: 16, height: 16, flexShrink: 0, opacity: 0.5 }}
                  />
                  {/* Existing tag chips */}
                  {searchTags.map(tag => (
                    <Box
                      key={tag}
                      sx={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        backgroundColor: '#6318C6', color: '#fff',
                        borderRadius: '20px', px: 1.2, py: 0.3,
                        fontSize: '12px', fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)',
                        fontWeight: 600, flexShrink: 0,
                      }}
                    >
                      {tag}
                      <Box
                        component="span"
                        onClick={(e) => { e.stopPropagation(); removeTagFromSearch(tag); }}
                        sx={{ cursor: 'pointer', lineHeight: 1, fontSize: '14px', ml: 0.3, opacity: 0.8, '&:hover': { opacity: 1 } }}
                      >
                        ×
                      </Box>
                    </Box>
                  ))}
                  {/* Tag text input */}
                  <input
                    ref={tagInputRef}
                    placeholder="Search by attributes"
                    type="text"
                    value={tagInput}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val.endsWith(',')) {
                        const tag = val.slice(0, -1).trim();
                        if (tag) addTagToSearch(tag);
                      } else {
                        setTagInput(val);
                      }
                    }}
                    onFocus={() => { if (tagInput.trim() && tagSuggestions.length > 0) setShowTagDropdown(true); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && tagInput.trim()) {
                        e.preventDefault();
                        addTagToSearch(tagInput.trim());
                      } else if (e.key === 'Backspace' && !tagInput && searchTags.length > 0) {
                        removeTagFromSearch(searchTags[searchTags.length - 1]);
                      }
                    }}
                    style={{
                      border: 'none', outline: 'none', flex: 1, minWidth: '120px',
                      fontSize: '13px', fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)',
                      color: '#1F2937', background: 'transparent', padding: 0,
                    }}
                  />
                  {/* Clear all button */}
                  {searchTags.length > 0 && (
                    <Box
                      component="span"
                      onClick={(e) => { e.stopPropagation(); setSearchTags([]); setSearchTerm(''); setTagInput(''); }}
                      sx={{ cursor: 'pointer', color: '#9ca3af', fontSize: '18px', lineHeight: 1, flexShrink: 0, '&:hover': { color: '#6318C6' } }}
                    >
                      ×
                    </Box>
                  )}
                </Box>
                {/* Autocomplete dropdown */}
                {showTagDropdown && (
                  <Box
                    ref={tagDropdownRef}
                    sx={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                      backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 100, overflow: 'hidden',
                      maxHeight: '290px',
                      overflowY: 'auto',
                      padding: '4px 0px 25px 0px',
                    }}
                  >
                    {tagSuggestionsLoading ? (
                      <Box sx={{ px: 2, py: 1.5, fontSize: '13px', color: '#9ca3af', fontFamily: 'var(--font-montserrat-medium)' }}>
                        Searching...
                      </Box>
                    ) : tagSuggestions.length === 0 ? (
                      <Box sx={{ px: 2, py: 1.5, fontSize: '13px', color: '#9ca3af', fontFamily: 'var(--font-montserrat-medium)' }}>
                        No matching tags found
                      </Box>
                    ) : (
                      tagSuggestions.map((suggestion, idx) => (
                        <Box
                          key={idx}
                          onMouseDown={(e) => { e.preventDefault(); addTagToSearch(suggestion); }}
                          sx={{
                            px: 2, py: 1.2, fontSize: '13px', cursor: 'pointer',
                            fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)',
                            color: '#1F2937',
                            '&:hover': { backgroundColor: '#f3f0ff', color: '#6318C6' },
                            borderBottom: idx < tagSuggestions.length - 1 ? '1px solid #f3f4f6' : 'none',
                            display: 'flex', alignItems: 'center', gap: 1,
                          }}
                        >
                          <Box component="span" sx={{ fontSize: '12px', opacity: 0.4 }}>
                            {typeof suggestion === 'string' || suggestion.type === 'Tag' ? <ChevronRightIcon /> : <ChevronRightIcon />}
                          </Box>
                          <Box component="span" sx={{ flexGrow: 1 }}>
                            {typeof suggestion === 'string' ? suggestion : suggestion.label}
                          </Box>
                          {suggestion.type && suggestion.type !== 'Tag' && (
                            <Box component="span" sx={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', ml: 'auto' }}>
                              {suggestion.type}
                            </Box>
                          )}
                        </Box>
                      ))
                    )}
                  </Box>
                )}
              </Box>
              {/* Filter toggle button and Dropdown Wrapper */}
              <Box ref={filterPanelRef} sx={{ position: { xs: 'static', md: 'relative' } }}>
                <Box
                  onClick={() => {
                    const isOpening = !filterPanelOpen;
                    setFilterPanelOpen(isOpening);
                    if (isOpening) {
                      syncTempFilters();
                    }
                  }}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '10px',
                    bgcolor: '#A78BFA',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0,
                    '&:hover': { bgcolor: '#9168f0' },
                    transition: 'background 0.2s',
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/img/icons/filter-icon.svg"
                    alt="filter"
                    sx={{ width: 20, height: 20 }}
                  />
                </Box>

                {/* Filter dropdown panel */}
                {filterPanelOpen && (
                  <Box
                    sx={{
                      mt: { xs: 1, md: 0 },
                      p: 2.5,
                      bgcolor: '#fff',
                      borderRadius: '16px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                      border: '1px solid #f0f0f0',
                      zIndex: 20,
                      position: 'absolute',
                      top: { xs: '100%', md: 0 },
                      left: { xs: 0, md: '100%' },
                      right: { xs: 0, md: 'auto' },
                      ml: { xs: 0, md: 2 },
                      width: { xs: 'auto', md: 'max-content' },
                      minWidth: { md: '450px' },
                      maxWidth: { md: '500px' }
                    }}
                  >
                    {/* Header: Filters | Apply  Clear All */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: 16, color: '#1E2939' }}>
                        Filters
                      </Typography>
                      <Box display="flex" gap={2} alignItems="center">
                        <Typography
                          onClick={applyFilters}
                          sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: 14, color: '#111827', cursor: 'pointer' }}
                        >
                          Apply
                        </Typography>
                        <Typography
                          onClick={() => {
                            // Clear both temporary and actual filter states
                            setStatusFilter('');
                            setStartDate('');
                            setEndDate('');
                            setSearchTerm('');
                            setDateError('');
                            setTempStatusFilter('');
                            setTempStartDate('');
                            setTempEndDate('');
                            setTempSearchTerm('');
                            setSearchTags([]);
                            setTagInput('');
                          }}
                          sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: 14, color: '#7C3AED', cursor: 'pointer' }}
                        >
                          Clear All
                        </Typography>
                      </Box>
                    </Box>

                    {/* Status section */}
                    <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: 14, color: '#364153', mb: 1.5 }}>
                      Status
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1} mb={3}>
                      {[
                        { label: 'All', value: '' },
                        { label: 'Completed', value: 'COMPLETED' },
                        { label: 'Active', value: 'PENDING_OR_PROCESSING' },
                        { label: 'Failed', value: 'FAILED' },
                      ].map(opt => (
                        <Box
                          key={opt.value}
                          onClick={() => setTempStatusFilter(opt.value)}
                          sx={{
                            px: 2.5,
                            py: 0.8,
                            borderRadius: '10px',
                            bgcolor: tempStatusFilter === opt.value ? '#EDE9FE' : '#F3F4F6',
                            color: tempStatusFilter === opt.value ? '#7C3AED' : '#6B7280',
                            border: '1px solid #E5E7EB',
                            fontFamily: 'var(--font-montserrat-medium)',
                            fontSize: 13,
                            fontWeight: 500,
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                            userSelect: 'none',
                          }}
                        >
                          {opt.label}
                        </Box>
                      ))}
                    </Box>

                    {/* Date Range section */}
                    <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: 14, color: '#364153', mb: 2 }}>
                      Date Range
                    </Typography>
                    {dateError && (
                      <Typography sx={{ color: '#ef4444', fontSize: 12, mb: 1 }}>{dateError}</Typography>
                    )}
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Box display="flex" gap={2} flexWrap="wrap">
                        {/* From date */}
                        <Box sx={{ flex: 1, minWidth: 130, position: 'relative' }}>
                          <Box
                            component="label"
                            sx={{
                              position: 'absolute',
                              top: '-4px',
                              left: '16px',
                              fontSize: '11px',
                              color: '#6B7280',
                              bgcolor: '#fff',
                              px: 0.5,
                              fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)',
                              zIndex: 2,
                              lineHeight: 1,
                            }}
                          >
                            From
                          </Box>
                          <DatePicker format="DD/MM/YYYY"
                            value={tempStartDate ? dayjs(tempStartDate) : null}
                            maxDate={dayjs().endOf('day')}
                            onChange={(newValue) => {
                              const dateStr = newValue ? newValue.format('YYYY-MM-DD') : '';
                              setTempStartDate(dateStr);
                              const err = validateDates(dateStr, tempEndDate);
                              setDateError(err);
                            }}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                InputProps: {
                                  sx: {
                                    height: '40px',
                                    borderRadius: '100px',
                                    fontSize: 12,
                                    fontFamily: 'var(--font-montserrat-medium)',
                                    color: '#1F2937',
                                    backgroundColor: '#fff',
                                    '& fieldset': {
                                      border: '1.5px solid #D1D5DB',
                                    },
                                    '&:hover fieldset': {
                                      border: '1.5px solid #9CA3AF',
                                    },
                                    '&.Mui-focused fieldset': {
                                      border: '1.5px solid #1E293B',
                                    },
                                    '& .MuiInputBase-input': {
                                      padding: '0 14px',
                                      height: '40px',
                                      boxSizing: 'border-box'
                                    }
                                  }
                                }
                              }
                            }}
                          />
                        </Box>
                        {/* To date */}
                        <Box sx={{ flex: 1, minWidth: 130, position: 'relative' }}>
                          <Box
                            component="label"
                            sx={{
                              position: 'absolute',
                              top: '-4px',
                              left: '16px',
                              fontSize: '11px',
                              color: '#6B7280',
                              bgcolor: '#fff',
                              px: 0.5,
                              fontFamily: 'var(--font-montserrat-medium)',
                              zIndex: 2,
                              lineHeight: 1,
                            }}
                          >
                            To
                          </Box>
                          <DatePicker format="DD/MM/YYYY"
                            value={tempEndDate ? dayjs(tempEndDate) : null}
                            maxDate={dayjs().endOf('day')}
                            onChange={(newValue) => {
                              const dateStr = newValue ? newValue.format('YYYY-MM-DD') : '';
                              setTempEndDate(dateStr);
                              const err = validateDates(tempStartDate, dateStr);
                              setDateError(err);
                            }}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                InputProps: {
                                  sx: {
                                    height: '40px',
                                    borderRadius: '100px',
                                    fontSize: 12,
                                    fontFamily: 'var(--font-montserrat-medium)',
                                    color: '#1F2937',
                                    backgroundColor: '#fff',
                                    '& fieldset': {
                                      border: '1.5px solid #D1D5DB',
                                    },
                                    '&:hover fieldset': {
                                      border: '1.5px solid #9CA3AF',
                                    },
                                    '&.Mui-focused fieldset': {
                                      border: '1.5px solid #1E293B',
                                    },
                                    '& .MuiInputBase-input': {
                                      padding: '0 14px',
                                      height: '40px',
                                      boxSizing: 'border-box'
                                    }
                                  }
                                }
                              }
                            }}
                          />
                        </Box>
                      </Box>
                    </LocalizationProvider>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {/* Generation List - Responsive Layout */}
          <Box>
            {/* Skeleton rows while first page loads (avoids empty list under LinearProgress) */}
            {loading && generations.length === 0 && (
              <Box sx={{ mb: 0 }}>
                {Array.from({ length: HISTORY_ITEMS_PER_PAGE }).map((_, idx) => (
                  <Card
                    key={`history-initial-skel-${idx}`}
                    elevation={0}
                    sx={{ mb: 2, border: '1px solid #e5e7eb', borderRadius: '8px', p: 2 }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Skeleton variant="circular" width={45} height={45} animation="wave" />
                          <Box sx={{ flex: 1 }}>
                            <Skeleton variant="text" width="60%" height={24} animation="wave" sx={{
                              '&::after': {
                                background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.60) 50%, rgba(255,255,255,0) 100%)',
                                animationDuration: '0.9s',
                                animationTimingFunction: 'linear'
                              }
                            }} />
                            <Skeleton variant="text" width="40%" height={20} animation="wave" sx={{
                              '&::after': {
                                background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.60) 50%, rgba(255,255,255,0) 100%)',
                                animationDuration: '0.9s',
                                animationTimingFunction: 'linear'
                              }
                            }} />
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 2 }}>
                          <Skeleton variant="rectangular" width={120} height={36} animation="wave" sx={{
                            '&::after': {
                              background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.60) 50%, rgba(255,255,255,0) 100%)',
                              animationDuration: '0.9s',
                              animationTimingFunction: 'linear'
                            }
                          }} />
                          <Skeleton variant="rectangular" width={140} height={36} animation="wave" sx={{
                            '&::after': {
                              background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.60) 50%, rgba(255,255,255,0) 100%)',
                              animationDuration: '0.9s',
                              animationTimingFunction: 'linear'
                            }
                          }} />
                        </Box>
                      </Grid>
                    </Grid>
                  </Card>
                ))}
              </Box>
            )}

            {generations.map((generation) => (
              <Card
                key={generation.id}
                elevation={0}
                sx={{
                  mb: { xs: 2, md: 3 },
                  width: '100%',
                  borderRadius: '17.53px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0px 2.19px 8.77px 0px rgba(0,0,0,0.08)',
                  p: 2,
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'stretch', md: 'flex-start' },
                  minHeight: { xs: 'auto', md: 110 },
                  overflow: 'visible',
                  zIndex: editingTagsId === generation.id ? 10 : 1,
                  position: 'relative',
                }}
              >


                {/* Mobile & Desktop Responsive Design Layout */}
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'stretch', md: 'center' },
                  justifyContent: 'space-between',
                  width: '100%',
                  gap: { xs: 0, md: 3 }
                }}>

                  {/* Row 1: Image (90x90) + Info */}
                  <Box display="flex" alignItems="center" gap={2} mb={{ xs: 1.5, md: 0 }} sx={{ flex: { md: 1 }, minWidth: 0 }}>
                    {/* Image / Video thumbnail 90x90 */}
                    <Box
                      className="gen-thumbnail"
                      onClick={() => {
                        // Act like Preview button click
                        if (generation.status?.name === 'COMPLETED' && canPreview && generation.is_downloadable) {
                          handlePreview(generation);
                        } else if (generation.status?.name === 'COMPLETED' && !generation.is_downloadable) {
                          // Allow viewing even if not downloadable if that's standard, but fallback to handlePreview
                          handlePreview(generation);
                        }
                      }}
                      sx={{ cursor: generation.status?.name === 'COMPLETED' ? 'pointer' : 'default', position: 'relative' }}
                    >
                      {/* REEL label — UI overlay (never cropped by object-fit cover) */}
                      {generation.jewelry_category?.display_name === 'Reel' && (
                        <Box sx={{
                          position: 'absolute', top: 3, left: 3, zIndex: 5, pointerEvents: 'none',
                          display: 'flex', alignItems: 'center', gap: '2px', px: '5px', py: '2px',
                          background: 'linear-gradient(90deg, #A864ED 0%, #7B3FE4 100%)',
                          borderRadius: '3px 9px 9px 3px',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.30)',
                        }}>
                          <PlayArrow sx={{ color: '#fff', fontSize: 10 }} />
                          <Typography sx={{ color: '#fff', fontSize: 8, fontWeight: 700, lineHeight: 1, letterSpacing: '0.3px' }}>
                            REEL
                          </Typography>
                        </Box>
                      )}
                      {generation.status?.name === 'COMPLETED' && generation.output_format?.name === 'VIDEO' ? (
                        generation.enhanced_image_path ? (
                          <Box className="gen-video-preview">
                            <SecureImage
                              src={getSecureThumbnailUrl(generation.id, "400x400", "enhanced")}
                              alt="video preview" useThumbnail={true} thumbnailSize="400x400"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              {...HISTORY_LIST_THUMB_IMG_HINTS}
                            />
                            <Box className="gen-play-btn">
                              <PlayArrow sx={{ color: '#fff', fontSize: 18 }} />
                            </Box>
                          </Box>
                        ) : (
                          <Box className="gen-video-placeholder">
                            <PlayArrow sx={{ color: '#fff', fontSize: 32 }} />
                          </Box>
                        )
                      ) : generation.status?.name === 'COMPLETED' ? (
                        <SecureImage
                          src={getSecureThumbnailUrl(generation.id, "400x400")}
                          alt="preview" useThumbnail={true} thumbnailSize="400x400"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          {...HISTORY_LIST_THUMB_IMG_HINTS}
                        />
                      ) : generation.status?.name === 'FAILED' ? (
                        <ImageIcon sx={{ color: '#9ca3af', fontSize: 32 }} />
                      ) : (
                        <CircularProgress size={24} sx={{ color: '#6318C6' }} />
                      )}
                    </Box>

                    {/* Right side: category, status + token cost, date */}
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'flex-start'
                      }}
                    >
                      {/* Category Title */}
                      <Typography className="gen-category-title">
                        {generation.title || generation.jewelry_category?.display_name || 'Category'}
                      </Typography>

                      {/* Status + token cost */}
                      <Box display="flex" alignItems="center" gap={1} mb={0.75} flexWrap="wrap">
                        {(generation.status?.name === 'PROCESSING' || generation.status?.name === 'PENDING') && (
                          <Box className="gen-badge-processing">Processing</Box>
                        )}
                        {generation.status?.name === 'COMPLETED' && (
                          <Box className="gen-badge-completed">Completed</Box>
                        )}
                        {generation.status?.name === 'FAILED' && (
                          <Box className="gen-badge-failed">Failed</Box>
                        )}
                        {generation.status?.name === 'CANCELLED' && (
                          <Box className="gen-badge-cancelled">Cancelled</Box>
                        )}

                        <Box className="gen-badge-tokens">
                          {formatCredits(generation.credits_cost)} {FlutterServicesAPI.getCreditLabel().toLowerCase()}
                        </Box>
                      </Box>

                      {/* Date */}
                      <Typography className="gen-datetime">
                        {new Date(generation.created_at).toLocaleString('en-IN', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit', second: '2-digit',
                          hour12: true,
                          timeZone: 'Asia/Kolkata'
                        })}
                      </Typography>

                      {/* Notes */}
                      {/* Commented out - hiding refund information
                      {generation.is_refunded && (
                        <Typography sx={{ fontSize: 11, color: '#16a34a', fontStyle: 'italic', mt: 0.5 }}>
                          Refunded: {formatCredits(generation.refund_amount)} {FlutterServicesAPI.getCreditLabel().toLowerCase()}
                        </Typography>
                      )}

                      {generation.status?.name === 'FAILED' && (
                        <Typography sx={{ fontSize: 11, color: '#b91c1c', fontStyle: 'italic', mt: 0.5 }}>
                          {FlutterServicesAPI.getCreditLabel()} not deducted
                        </Typography>
                      )}
                      */}
                    </Box>
                  </Box>

                  {/* Custom Tags Section */}
                  <Box mb={{ xs: 1.5, md: 0 }} sx={{ flex: { md: 1 }, minWidth: 0, px: { md: 2 } }}>
                    {/* Title row with edit icon */}
                    <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                      <Typography className="gen-tags-title" sx={{ mb: '0 !important' }}>
                        Custom Tags
                      </Typography>
                      <Tooltip title="Edit tags" placement="top">
                        <IconButton
                          size="small"
                          onClick={() => handleStartTagEdit(generation)}
                          sx={{
                            p: '4px',
                            color: '#651BC6',
                            '&:hover': { backgroundColor: '#f3f0ff' },
                            '&:hover svg': { color: '#5514B4' }
                          }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {editingTagsId === generation.id ? (
                      /* Inline editor mode */
                      <Box>
                        <HistTagsContainer>
                          {tagEditState.tags.map((tag, i) => (
                            <HistTagChip
                              key={`${tag}-${i}`}
                              label={tag}
                              onClick={() =>
                                setTagEditState(prev => ({
                                  ...prev,
                                  tags: prev.tags.filter(t => t !== tag),
                                  input: tag
                                }))
                              }
                              onDelete={() =>
                                setTagEditState(prev => ({
                                  ...prev,
                                  tags: prev.tags.filter(t => t !== tag)
                                }))
                              }
                              deleteIcon={<Close />}
                              sx={{ cursor: 'pointer' }}
                            />
                          ))}
                          <Box sx={{ position: 'relative', flex: 1, display: 'flex' }}>
                            <HistTagInput
                              placeholder={tagEditState.tags.length === 0 ? 'Add tags…' : ''}
                              value={tagEditState.input}
                              onChange={e => {
                                const val = e.target.value;
                                if (val.endsWith(',') || val.endsWith(' ')) {
                                  const tag = val.slice(0, -1).trim();
                                  if (tag) {
                                    setTagEditState(prev => {
                                      const existing = new Set(prev.tags.map(t => t.toLowerCase()));
                                      if (!existing.has(tag.toLowerCase())) {
                                        return { ...prev, tags: [...prev.tags, tag], input: '' };
                                      }
                                      return { ...prev, input: '' };
                                    });
                                  } else {
                                    setTagEditState(prev => ({ ...prev, input: '' }));
                                  }
                                } else {
                                  setTagEditState(prev => ({ ...prev, input: val }));
                                }
                              }}
                              onFocus={() => setIsTagInputFocused(true)}
                              onBlur={() => setTimeout(() => setIsTagInputFocused(false), 200)}
                              onKeyDown={handleHistTagKeyDown}
                              autoFocus
                            />
                            {isTagInputFocused && tagEditState.input.trim().length >= minTagSuggestChars && customTagSuggestions.length > 0 && (
                              <Paper
                                elevation={3}
                                sx={{
                                  position: 'absolute',
                                  top: 'calc(100% + 6px)',
                                  left: 0,
                                  right: 0,
                                  zIndex: 30,
                                  borderRadius: '8px',
                                  border: '1px solid #E5E7EB',
                                  maxHeight: '180px',
                                  overflowY: 'auto'
                                }}
                              >
                                {customTagSuggestions.map((suggestion, sIdx) => (
                                  <Box
                                    key={sIdx}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleTagSuggestionSelect(suggestion)}
                                    sx={{
                                      px: 1.5,
                                      py: 1,
                                      fontSize: '14px',
                                      cursor: 'pointer',
                                      borderBottom: '1px solid #F3F4F6',
                                      '&:last-of-type': { borderBottom: 'none' },
                                      '&:hover': { backgroundColor: '#F9FAFB' },
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1
                                    }}
                                  >
                                    <Box component="span" sx={{ fontSize: '12px', opacity: 0.4 }}>
                                      {typeof suggestion === 'string' || suggestion.type === 'Tag' ? '🏷' : '✨'}
                                    </Box>
                                    <Box component="span" sx={{ flexGrow: 1 }}>
                                      {typeof suggestion === 'string' ? suggestion : suggestion.label}
                                    </Box>
                                    {suggestion.type && suggestion.type !== 'Tag' && (
                                      <Box component="span" sx={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase' }}>
                                        {suggestion.type}
                                      </Box>
                                    )}
                                  </Box>
                                ))}
                              </Paper>
                            )}
                          </Box>
                        </HistTagsContainer>
                        <Typography sx={{ fontSize: '11px', color: '#6B7280', mt: 0.5 }}>
                          *Add comma after each tag.
                        </Typography>
                        {tagEditState.error && (
                          <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                            {tagEditState.error}
                          </Typography>
                        )}
                        <Box display="flex" gap={1} mt={1}>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={tagEditState.saving}
                            onClick={() => handleSaveHistoryTags(generation.id)}
                            sx={{
                              textTransform: 'none',
                              borderRadius: '8px',
                              background: 'linear-gradient(90deg, #651BC6 0%, #EAA24A 100%)',
                              boxShadow: 'none',
                              fontSize: '12px',
                              py: 0.5,
                              px: 2,
                              fontFamily: 'var(--font-montserrat-semibold)',
                              '&:hover': { opacity: 0.9, boxShadow: 'none' }
                            }}
                          >
                            {tagEditState.saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setEditingTagsId(null)}
                            sx={{ textTransform: 'none', fontSize: '12px', color: '#6B7280', py: 0.5 }}
                          >
                            Cancel
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      /* Read-only view mode */
                      <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center">
                        {(() => {
                          const tags = parseHistoryTags(generation);
                          const isExpanded = expandedTags[generation.id];
                          const MAX = 4;
                          const visible = isExpanded ? tags : tags.slice(0, MAX);
                          const extra = tags.length - MAX;
                          return (
                            <>
                              {tags.length === 0 ? (
                                <Typography sx={{ fontSize: '13px', color: '#9CA3AF', fontFamily: 'var(--font-montserrat-medium)' }}>
                                  No tags added
                                </Typography>
                              ) : (
                                <>
                                  {visible.map((tag, i) => (
                                    <Box key={i} className="gen-tag-item">{tag}</Box>
                                  ))}
                                  {!isExpanded && extra > 0 && (
                                    <Box
                                      onClick={() => setExpandedTags(prev => ({ ...prev, [generation.id]: true }))}
                                      className="gen-tag-extra"
                                    >
                                      +{extra}
                                    </Box>
                                  )}
                                  {isExpanded && (
                                    <Box
                                      onClick={() => setExpandedTags(prev => ({ ...prev, [generation.id]: false }))}
                                      className="gen-tag-less"
                                    >
                                      Show less
                                    </Box>
                                  )}
                                </>
                              )}
                            </>
                          );
                        })()}
                      </Box>
                    )}
                  </Box>

                  {/* Batch tag */}
                  {/* {generation.batch_id && (
                    <Box mb={1.5}>
                      <Tooltip title={`Batch Generation - Full ID: ${generation.batch_id}`} placement="top" arrow>
                        <Chip label="BATCH" size="small" sx={{ bgcolor: '#fef3c7', color: '#d97706', fontWeight: 700, borderRadius: 2, fontSize: 11, letterSpacing: 0.5, cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                  )} */}

                  {/* Action Buttons: 2x2 grid */}
                  <Box className="action-button-grid" sx={{ width: { xs: '100%', md: '350px' }, flexShrink: 0 }}>
                    {canPreview && (
                      <Button
                        variant="outlined"
                        className="action-btn"
                        onClick={() => handlePreview(generation)}
                        disabled={!generation.is_downloadable}
                        fullWidth
                      >
                        Preview
                      </Button>
                    )}
                    {canDownload && (
                      <Button
                        variant="outlined"
                        className="action-btn"
                        onClick={() => handleDownload(generation)}
                        disabled={!generation.is_downloadable}
                        fullWidth
                      >
                        {FlutterServicesAPI.isFlutterApp() ? 'Save' : 'Download'}
                      </Button>
                    )}
                    {canRegenerate && canRegenerateGeneration(generation) && (
                      <Button
                        variant="outlined"
                        className="action-btn"
                        onClick={() => handleRegenerate(generation)}
                        fullWidth
                      >
                        Regenerate
                      </Button>
                    )}
                    <Tooltip title={isFeedbackDisabled(generation) ? (generation.status?.name === 'PENDING' || generation.status?.name === 'PROCESSING' ? 'Feedback not available while processing' : 'Feedback not available for refunded generations') : ''}>
                      <span style={{ display: 'contents' }}>
                        {canSubmitFeedback && canHaveFeedback(generation) && (
                          hasFeedback(generation) ? (
                            <Button
                              variant="outlined"
                              className="action-btn"
                              onClick={() => handleViewFeedback(generation)}
                              fullWidth
                            >
                              View Feedback
                            </Button>
                          ) : (
                            <Button
                              variant="outlined"
                              className="action-btn"
                              onClick={() => { setSelectedGeneration(generation); handleFeedback(); }}
                              disabled={isFeedbackDisabled(generation)}
                              fullWidth
                            >
                              Feedback
                            </Button>
                          )
                        )}
                      </span>
                    </Tooltip>
                  </Box>
                </Box>

              </Card>
            ))}
          </Box>

          {generations.length === 0 && !loading && (
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary">
                {searchTerm ? 'No generations found matching your search' : 'No generations found'}
              </Typography>
              <Typography sx={{ fontFamily: 'var(--font-montserrat-regular)', fontWeight: 400, fontSize: 13, color: '#6B7280', mt: 1, textAlign: 'center' }}>
                {searchTerm
                  ? `No results found for "${searchTerm}". Try different keywords or check your spelling.`
                  : 'Try adjusting your filters or check back later for new generations.'
                }
              </Typography>
              {searchTerm && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setSearchTerm('');
                      setTempSearchTerm('');
                      setSearchTags([]);
                      setTagInput('');
                    }}
                    sx={{ mt: 2 }}
                  >
                    Clear Search
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {/* Next page skeletons */}
          {showNextSkeleton && (
            <Box sx={{ mt: 2 }}>
              {Array.from({ length: HISTORY_ITEMS_PER_PAGE }).map((_, idx) => (
                <Card key={`skeleton-${idx}`} elevation={0} sx={{ mb: 2, border: '1px solid #e5e7eb', borderRadius: '8px', p: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Skeleton variant="circular" width={45} height={45} animation="wave" />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton variant="text" width="60%" height={24} animation="wave" sx={{
                            '&::after': {
                              background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.60) 50%, rgba(255,255,255,0) 100%)',
                              animationDuration: '0.9s',
                              animationTimingFunction: 'linear'
                            }
                          }} />
                          <Skeleton variant="text" width="40%" height={20} animation="wave" sx={{
                            '&::after': {
                              background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.60) 50%, rgba(255,255,255,0) 100%)',
                              animationDuration: '0.9s',
                              animationTimingFunction: 'linear'
                            }
                          }} />
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 2 }}>
                        <Skeleton variant="rectangular" width={120} height={36} animation="wave" sx={{
                          '&::after': {
                            background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.60) 50%, rgba(255,255,255,0) 100%)',
                            animationDuration: '0.9s',
                            animationTimingFunction: 'linear'
                          }
                        }} />
                        <Skeleton variant="rectangular" width={140} height={36} animation="wave" sx={{
                          '&::after': {
                            background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.60) 50%, rgba(255,255,255,0) 100%)',
                            animationDuration: '0.9s',
                            animationTimingFunction: 'linear'
                          }
                        }} />
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              ))}
            </Box>
          )}

          {/* Infinite scroll sentinel */}
          <Box ref={sentinelRef} sx={{ height: 1 }} />
        </Paper>

        <GenerationPreviewDialog
          key={selectedGeneration?.id}
          open={previewDialog}
          onClose={() => setPreviewDialog(false)}
          generation={selectedGeneration}
          onDownloadSuccess={() => loadGenerations()}
        />

        {/* Feedback Dialog */}
        <Dialog
          open={feedbackDialog}
          onClose={() => setFeedbackDialog(false)}
          maxWidth="sm"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              width: { xs: '95vw', sm: '90vw', md: '500px' },
              margin: { xs: '10px', sm: '20px', md: 'auto' },
              borderRadius: '24px' // Smooth look matching the image
            }
          }}
        >
          <DialogTitle sx={{
            fontSize: '16px',
            color: '#101828',
            fontFamily: 'var(--font-montserrat-semibold, Montserrat, sans-serif)',
            fontWeight: 600,
            px: { xs: 2.5, sm: 3 },
            py: 2,
            borderBottom: '1px solid #E5E7EB',
            textAlign: 'left'
          }}>
            Submit Feedback - Generation #{selectedGeneration?.id || '1'}
          </DialogTitle>
          <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, py: 3, pb: 4 }}>
            {selectedGeneration ? (
              <Box sx={{
                display: { md: 'flex' },
                flexDirection: { md: 'row' },
                gap: { md: 2 },
                alignItems: { md: 'flex-start' }
              }}>
                {/* Left Side: Media Preview (Desktop) or Top (Mobile) */}
                <Box sx={{ flex: { md: 1 }, width: '100%', mb: { xs: 3, md: 0 } }}>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                    mt: { xs: 2, md: 2 }
                  }}>
                    <Typography sx={{
                      fontSize: '16px',
                      fontFamily: 'var(--font-montserrat-semibold, Montserrat, sans-serif)',
                      color: '#000000',
                      fontWeight: 600
                    }}>
                      Generation Output
                    </Typography>
                    {/* Category name - shown only on mobile here for consistency OR always show if needed */}
                    <Typography sx={{
                      display: { md: 'none' },
                      fontSize: '16px',
                      fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)',
                      color: '#000000',
                      fontWeight: 500
                    }}>
                      {selectedGeneration.jewelry_category?.display_name || 'Category'}
                    </Typography>
                  </Box>

                  <Box sx={{
                    position: 'relative',
                    width: '100%',
                    borderRadius: '10.29px',
                    overflow: 'hidden',
                    // Revert to original behavior for mobile (minHeight) vs desktop (aspectRatio)
                    aspectRatio: { xs: 'auto', md: '3/4' },
                    minHeight: { xs: '350px', md: 'auto' },
                    backgroundColor: '#f3f4f6'
                  }}>
                    {/* Top right badge */}
                    <Box sx={{
                      position: 'absolute',
                      top: { xs: 12, md: 4 },
                      right: { xs: 12, md: 5 },
                      bgcolor: '#FFFFFFB8',
                      borderRadius: '35.2px',
                      height: { xs: '32px', md: '28px' },
                      px: { xs: 2.5, md: 1.5 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10,
                      backdropFilter: 'blur(4px)',
                      color: '#651BC6',
                      fontSize: { xs: '13px', md: '10px' },
                      fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)',
                      fontWeight: 500
                    }}>
                      {selectedGeneration.output_format?.display_name || selectedGeneration.output_format?.name || 'Image'}
                    </Box>

                    {/* Zoom/Play icon bottom left */}
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        if (selectedGeneration.output_format?.name === 'VIDEO') {
                          handlePlayMedia(getSecureVideoStreamUrl(selectedGeneration.id), 'video', selectedGeneration.id);
                        } else {
                          handleZoomImage(getSecureThumbnailUrl(selectedGeneration.id, '1200x1200'), 'image', selectedGeneration.id);
                        }
                      }}
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        left: 12,
                        zIndex: 10,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                          transform: 'scale(1.1)',
                        },
                        transition: 'transform 0.2s ease',
                      }}
                    >
                      <img
                        src={selectedGeneration.output_format?.name === 'VIDEO' ? "/assets/img/icons/Play-icon.svg" : "/assets/img/icons/zoom-imge.svg"}
                        alt={selectedGeneration.output_format?.name === 'VIDEO' ? "Play" : "Zoom"}
                        style={{ width: 45, height: 45 }}
                      />
                    </Box>

                    {/* Media content */}
                    {selectedGeneration.output_format?.name === 'VIDEO' ? (
                      <Box
                        onClick={() => handlePlayMedia(getSecureVideoStreamUrl(selectedGeneration.id), 'video', selectedGeneration.id)}
                        sx={{ width: '100%', height: { xs: 'auto', md: '100%' }, minHeight: { xs: '350px' }, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <SecureVideo
                          src={getSecureVideoStreamUrl(selectedGeneration.id)}
                          poster={getSecureThumbnailUrl(selectedGeneration.id, '1200x1200', 'enhanced')}
                          controls={false}
                          style={{
                            width: '100%',
                            height: '100%',
                            maxHeight: '400px',
                            objectFit: 'cover',
                            borderRadius: '10.29px',
                            display: 'block'
                          }}
                        />
                      </Box>
                    ) : (
                      <Box
                        onClick={() => handleZoomImage(getSecureThumbnailUrl(selectedGeneration.id, '1200x1200'), 'image', selectedGeneration.id)}
                        sx={{ width: '100%', height: { xs: 'auto', md: '100%' }, minHeight: { xs: '350px' }, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <SecureImage
                          src={getSecureThumbnailUrl(selectedGeneration.id, '1200x1200')}
                          alt="Generation preview"
                          useThumbnail={true}
                          thumbnailSize="1200x1200"
                          style={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '10.29px',
                            display: 'block'
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Right Side: Feedback Form (Desktop) or Bottom (Mobile) */}
                <Box sx={{ flex: { md: 1.2 }, width: '100%' }}>
                  {/* Category info - shown only on desktop here */}
                  <Box sx={{ display: { xs: 'none', md: 'flex' }, mt: { md: 2 }, justifyContent: 'flex-end', mb: 2 }}>
                    <Typography sx={{ fontSize: '16px', fontFamily: 'var(--font-montserrat-semibold, Montserrat, sans-serif)', color: '#000000', fontWeight: 600 }}>
                      {selectedGeneration.jewelry_category?.display_name || 'Category'}
                    </Typography>
                  </Box>

                  {/* Rating Section */}
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ fontSize: '15px', color: '#101828', fontFamily: 'var(--font-montserrat-semibold, Montserrat, sans-serif)', fontWeight: 600, mb: 1.5 }}>
                      Rate This Generation <span style={{ color: '#FB2C36' }}>*</span>
                    </Typography>
                    <Rating
                      value={feedbackRating}
                      onChange={(event, newValue) => {
                        setFeedbackRating(newValue);
                      }}
                      icon={<Star sx={{ fontSize: '32px', color: '#F2CC00' }} />}
                      emptyIcon={<StarBorder sx={{ fontSize: '32px', color: '#D1D5DB' }} />}
                      sx={{ gap: '8px' }}
                    />
                  </Box>

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}

                  {/* Comments Section */}
                  <Typography sx={{ fontSize: '15px', color: '#101828', fontFamily: 'var(--font-montserrat-semibold, Montserrat, sans-serif)', fontWeight: 600, mb: 1 }}>
                    Additional Comments (Optional)
                  </Typography>
                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      minRows={{ xs: 4, md: 6 }}
                      placeholder="Share your thoughts about this generation"
                      value={feedbackComment}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= 500) {
                          setFeedbackComment(value);
                        }
                      }}
                      variant="outlined"
                      error={!!feedbackError}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12.12px',
                          backgroundColor: '#fff',
                          alignItems: 'flex-start',
                          fontFamily: 'var(--font-montserrat-regular, Montserrat, sans-serif)',
                          fontSize: '12.12px',
                          color: '#101828',
                          padding: '12px',
                          '& fieldset': {
                            borderColor: feedbackError ? '#ef4444' : '#E5E7EB',
                          },
                          '&:hover fieldset': {
                            borderColor: feedbackError ? '#ef4444' : '#E5E7EB',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: feedbackError ? '#ef4444' : '#D1D5DB',
                          },
                        },
                        '& .MuiInputBase-input::placeholder': {
                          color: '#99A1AF',
                          opacity: 1,
                          fontFamily: 'var(--font-montserrat-regular, Montserrat, sans-serif)',
                          fontSize: '12.12px',
                        },
                        '& .MuiInputBase-input': {
                          minHeight: { xs: '80px !important', md: 'auto' },
                          height: { xs: '80px !important', md: 'auto' }
                        }
                      }}
                    />
                  </Box>
                  {/* Character counter */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Typography
                      sx={{
                        fontSize: '10.39px',
                        fontFamily: 'var(--font-montserrat-regular, Montserrat, sans-serif)',
                        color: '#050505'
                      }}
                    >
                      {feedbackComment.length}/500 characters
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '15px' }}>
                  No generation selected
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{
            px: { xs: 2.5, sm: 3 },
            py: 2.5,
            borderTop: '1px solid #E5E7EB',
            boxShadow: '0px -3.48px 10.43px 0px #0000000D',
            background: '#fff',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'center' }}>
              <Button
                onClick={() => setFeedbackDialog(false)}
                sx={{
                  flex: 1,
                  maxWidth: '160px',
                  borderRadius: '10px',
                  backgroundColor: '#F3F4F6',
                  color: '#6B7280',
                  border: '1.6px solid #E5E7EB',
                  height: '36px',
                  fontWeight: 500,
                  textTransform: 'none',
                  fontSize: '14px',
                  fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)',
                  '&:hover': {
                    backgroundColor: '#E5E7EB',
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmitFeedback}
                disabled={feedbackSubmitting || !feedbackRating}
                sx={{
                  flex: 1,
                  maxWidth: '160px',
                  borderRadius: '10px',
                  backgroundColor: '#651BC6',
                  color: '#fff',
                  border: '1px solid #651BC6',
                  height: '36px',
                  fontWeight: 500,
                  textTransform: 'none',
                  fontSize: '14px',
                  fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#5015a1',
                    border: '1px solid #5015a1',
                    boxShadow: 'none',
                  },
                  '&:disabled': {
                    backgroundColor: '#d1d5db',
                    borderColor: '#d1d5db',
                    color: '#9ca3af',
                  },
                }}
              >
                {feedbackSubmitting ? (
                  <>
                    <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
                    Submitting...
                  </>
                ) : (
                  'Submit'
                )}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Feedback View Dialog */}
        <Dialog
          open={feedbackViewDialog}
          onClose={() => setFeedbackViewDialog(false)}
          maxWidth="xs"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: '17.39px',
              width: { xs: '92vw', sm: '500px' },
              maxWidth: '960px',
              minHeight: { sm: '480px' },
              maxHeight: '95vh',
              margin: { xs: '10px', sm: 'auto' },
              overflow: 'hidden',
            }
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #E5E7EB',
            px: 2.5,
            py: 2,
          }}>
            <Typography
              sx={{
                fontSize: '17.39px',
                fontFamily: 'var(--font-montserrat-semibold)',
                fontWeight: 600,
                color: '#000000',
                lineHeight: 1.3,
              }}
            >
              View Feedback - Generation #{selectedGeneration?.id || 'N/A'}
            </Typography>
            <IconButton
              onClick={() => setFeedbackViewDialog(false)}
              size="small"
              sx={{
                bgcolor: '#651BC6',
                color: 'white',
                width: 30,
                height: 30,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  bgcolor: '#5115a3',
                },
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M11.7376 3.91248L3.9126 11.7375" stroke="white" stroke-width="1.63021" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M3.9126 3.91248L11.7376 11.7375" stroke="white" stroke-width="1.63021" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </IconButton>
          </DialogTitle>
          <DialogContent className="preview-content" sx={{ p: { xs: 2, sm: 2.5 } }}>
            {selectedGeneration ? (
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 2 },
                paddingTop: { sm: 1 },
                alignItems: { xs: 'stretch', sm: 'flex-start' }
              }}>
                {/* Left Side: Media Preview */}
                <Box sx={{ width: { xs: '100%', sm: '190px' }, flexShrink: 0 }}>
                  <Typography sx={{
                    fontSize: '16px',
                    fontFamily: 'var(--font-montserrat-semibold, Montserrat, sans-serif)',
                    color: '#000000',
                    fontWeight: 600,
                    mb: 1
                  }}>
                    Generation Output
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Typography variant="body2" sx={{
                      fontSize: '14px',
                      fontFamily: 'var(--font-montserrat-semibold, Montserrat, sans-serif)',
                      color: '#000000',
                      fontWeight: 600,
                    }}>
                      {selectedGeneration.jewelry_category?.display_name || 'Category'}: {selectedGeneration.output_format?.display_name || 'Format'}
                    </Typography>
                  </Box>

                  {/* Display the generation output */}
                  <Box sx={{
                    width: '100%',
                    position: 'relative',
                    borderRadius: '9.16px',
                    overflow: 'hidden',
                    boxShadow: '0px 1.83px 9.16px 0px rgba(0,0,0,0.07)',
                    backgroundColor: '#f5f5f5',
                    aspectRatio: '3/5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {selectedGeneration.output_format?.name === 'VIDEO' ? (
                      <Box
                        onClick={() => handlePlayMedia(getSecureVideoStreamUrl(selectedGeneration.id), 'video', selectedGeneration.id)}
                        sx={{ position: 'relative', width: '100%', height: '100%', cursor: 'pointer' }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 10,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <img
                            src="/assets/img/icons/Play-icon.svg"
                            alt="Play"
                            style={{ width: 45, height: 45 }}
                          />
                        </Box>
                        <SecureVideo
                          src={getSecureVideoStreamUrl(selectedGeneration.id)}
                          poster={getSecureThumbnailUrl(selectedGeneration.id, '1200x1200', 'enhanced')}
                          controls={false}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block'
                          }}
                        />
                      </Box>
                    ) : (
                      <Box
                        onClick={() => handleZoomImage(getSecureThumbnailUrl(selectedGeneration.id, '1200x1200'), 'image', selectedGeneration.id)}
                        sx={{ position: 'relative', width: '100%', height: '100%', cursor: 'pointer' }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 8,
                            left: 12,
                            zIndex: 10,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover': {
                              transform: 'scale(1.1)',
                            },
                            transition: 'transform 0.2s ease',
                          }}
                        >
                          <img
                            src="/assets/img/icons/zoom-imge.svg"
                            alt="Zoom"
                            style={{ width: 45, height: 45 }}
                          />
                        </Box>
                        <SecureImage
                          src={getSecureThumbnailUrl(selectedGeneration.id, '1200x1200')}
                          alt="Generation preview"
                          useThumbnail={true}
                          thumbnailSize="1200x1200"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block'
                          }}
                        />
                      </Box>
                    )}
                  </Box>

                  <Typography variant="caption" color="text.secondary" sx={{
                    display: 'block',
                    mt: { xs: 1, sm: 1.5 },
                    textAlign: 'center',
                    fontSize: '11px'
                  }}>
                    Generation ID: #{selectedGeneration.id}
                  </Typography>
                </Box>

                {/* Right Side: Submitted Feedback */}
                <Box sx={{ flex: 1, width: '100%' }}>
                  <Typography sx={{
                    fontSize: '16px',
                    fontFamily: 'var(--font-montserrat-semibold, Montserrat, sans-serif)',
                    color: '#000000',
                    fontWeight: 600,
                    mb: 1
                  }}>
                    Your Feedback
                  </Typography>
                  <Box sx={{
                    p: { xs: 2, sm: 2.5 },
                    bgcolor: '#f0fdf4',
                    borderRadius: '9.16px',
                    border: '1px solid #bbf7d0',
                    borderLeft: '4px solid #10b981',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 2.5 }, flexGrow: 1 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                          Rating
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Rating
                            value={selectedGeneration.feedback_rating || 0}
                            readOnly
                            size="large"
                            sx={{
                              '& .MuiRating-iconFilled': {
                                color: '#f59e0b',
                              },
                            }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#101828' }}>
                            {selectedGeneration.feedback_rating > 0 ? `${selectedGeneration.feedback_rating} Star${selectedGeneration.feedback_rating > 1 ? 's' : ''}` : 'No rating'}
                          </Typography>
                        </Box>
                      </Box>

                      {selectedGeneration.feedback_comment && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                            Comment
                          </Typography>
                          <Box sx={{
                            p: 2,
                            bgcolor: '#fff',
                            borderRadius: '10px',
                            border: '1px solid #dcfce7'
                          }}>
                            <Typography variant="body2" color="#111827" sx={{
                              fontStyle: 'italic',
                              lineHeight: 1.6,
                              fontSize: '14px'
                            }}>
                              "{selectedGeneration.feedback_comment}"
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      <Box sx={{ mt: 'auto', pt: 2, borderTop: '1px solid #dcfce7' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '11px' }}>
                          Submitted on: {selectedGeneration.feedback_submitted_at ?
                            new Date(selectedGeneration.feedback_submitted_at).toLocaleString('en-IN', {
                              year: 'numeric',
                              month: 'short',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true,
                              timeZone: 'Asia/Kolkata',
                            }) : 'Unknown date'
                          }
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  No generation selected
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions className="dialog-actions-custom" sx={{ px: { xs: 2.5, sm: 2.5 }, pb: 2.5, justifyContent: 'center' }}>
            <Button
              onClick={() => setFeedbackViewDialog(false)}
              className="btn-primary p-12-0 font-14"
              sx={{
                flex: 1,
                maxWidth: { sm: '320px' },
                height: '42px !important',
                borderRadius: '10px !important',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textTransform: 'none',
                mx: 'auto',
                backgroundColor: '#10b981',
                color: 'white',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#059669',
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Enhanced Zoom Dialog */}
        <Dialog
          open={zoomDialog}
          onClose={() => {
            setZoomDialog(false);
            setIsVideoPlaying(false); // Reset video playing state when closing
          }}
          maxWidth={false}
          fullWidth={false}
          sx={{
            '& .MuiDialog-paper': {
              margin: 0,
              padding: 0,
              backgroundColor: '#000',
              boxShadow: 'none',
              maxWidth: 'none',
              maxHeight: 'none',
              width: '100vw',
              height: '100vh',
              overflow: 'hidden'
            }
          }}
        >
          {zoomedImage && (
            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
              <PinchZoomPanContainer>
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  {zoomedImage.type === 'video' ? (
                    <SecureVideo
                      src={zoomedImage.src}
                      poster={zoomedImage.generationId ? getSecureThumbnailUrl(zoomedImage.generationId, '1200x1200', 'enhanced') : null}
                      controls={true}
                      autoPlay={true}
                      muted={true}
                      style={{
                        maxWidth: '95vw',
                        maxHeight: '95vh',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        backgroundColor: '#000'
                      }}
                    />
                  ) : (
                    <SecureImage
                      src={zoomedImage.src}
                      alt={`Zoomed ${zoomedImage.type.toLowerCase()}`}
                      useThumbnail={false}
                      thumbnailSize="1200x1200"
                      style={{
                        maxWidth: '95vw',
                        maxHeight: '95vh',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        backgroundColor: '#000'
                      }}
                    />
                  )}
                  {/* Close Button - Attached to top right of media box */}
                  <IconButton
                    onClick={() => {
                      setZoomDialog(false);
                      setIsVideoPlaying(false);
                    }}
                    sx={{
                      position: 'absolute',
                      top: { xs: -12, md: -9 },
                      right: { xs: -9, md: -32 },
                      bgcolor: '#651BC6',
                      color: 'white',
                      '&:hover': {
                        bgcolor: '#5115a3',
                      },
                      width: { xs: 32, sm: 40 },
                      height: { xs: 32, sm: 40 },
                      borderRadius: '50%',
                      zIndex: 30,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M11.7376 3.91248L3.9126 11.7375" stroke="white" stroke-width="1.63021" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M3.9126 3.91248L11.7376 11.7375" stroke="white" stroke-width="1.63021" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </IconButton>
                </Box>
              </PinchZoomPanContainer>
            </Box>
          )}
        </Dialog>

        {/* Batch View Dialog */}
        <Dialog
          open={false} // This dialog is removed, so it's always closed.
          onClose={() => { }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Batch Details - {''}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom>
                All items in this batch:
              </Typography>

              <Grid container spacing={2} sx={{ mt: 2 }}>
                {/* This loop is removed as batchViewDialog is removed */}
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { }}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

// Secure Image Preview Component
const SecureImagePreview = ({ generationId, outputFormat }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [enhancedImageUrl, setEnhancedImageUrl] = useState(null);

  useEffect(() => {
    // Get secure URLs with JWT tokens - add cache buster to ensure we get latest watermarked version
    const timestamp = new Date().getTime();
    const mainUrl = getSecureThumbnailUrl(generationId, '1200x1200');
    setImageUrl(mainUrl);

    // Get enhanced image URL for videos
    if (outputFormat === 'VIDEO') {
      const enhancedUrl = getSecureThumbnailUrl(generationId, '1200x1200', 'enhanced');
      setEnhancedImageUrl(enhancedUrl);
    }
  }, [generationId, outputFormat]);

  if (!imageUrl) {
    return (
      <Box
        sx={{
          width: '100%',
          height: { xs: '60vh', sm: '50vh', md: 400 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          borderRadius: 1,
          flexDirection: 'column',
          gap: 1
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Preview failed to load
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Generation ID: {generationId}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Main generated image/video */}
      {outputFormat === 'VIDEO' ? (
        <SecureVideo
          src={imageUrl}
          poster={enhancedImageUrl}
          autoPlay={true}
          muted={true}
          style={{
            maxWidth: '100%',
            maxHeight: window.innerWidth < 768 ? '60vh' : '380px',
            objectFit: 'contain',
            borderRadius: '4px',
            zIndex: 2,
            position: 'relative'
          }}
          onLoad={(e) => {
            // Hide loading placeholder
            const loadingPlaceholder = document.getElementById('loading-placeholder');
            if (loadingPlaceholder) {
              loadingPlaceholder.style.display = 'none';
            }
          }}
          onError={(e) => {
            // Hide loading placeholder and show error
            const loadingPlaceholder = document.getElementById('loading-placeholder');
            if (loadingPlaceholder) {
              loadingPlaceholder.style.display = 'none';
            }
          }}
        />
      ) : (
        <SecureImage
          src={imageUrl}
          alt="Generated preview"
          style={{
            maxWidth: '100%',
            maxHeight: window.innerWidth < 768 ? '60vh' : '380px',
            objectFit: 'contain',
            borderRadius: '4px',
            zIndex: 2,
            position: 'relative'
          }}
          onLoad={(e) => {
            // Hide loading placeholder
            const loadingPlaceholder = document.getElementById('loading-placeholder');
            if (loadingPlaceholder) {
              loadingPlaceholder.style.display = 'none';
            }
          }}
          onError={(e) => {
            // Hide loading placeholder and show error
            const loadingPlaceholder = document.getElementById('loading-placeholder');
            if (loadingPlaceholder) {
              loadingPlaceholder.style.display = 'none';
            }
          }}
        />
      )}

      {/* Enhanced image for video generation */}
      {enhancedImageUrl && outputFormat === 'VIDEO' && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} color="#1e293b" gutterBottom>
            Enhanced Image (Step 1)
          </Typography>
          <SecureImage
            src={enhancedImageUrl}
            alt="Enhanced Image"
            style={{
              maxWidth: '100%',
              maxHeight: 400,
              objectFit: 'contain',
              borderRadius: '4px',
              border: '2px solid #f59e0b',
              backgroundColor: '#fef3c7'
            }}
            onError={(e) => {
              setEnhancedImageUrl(null);
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default GenerationHistory;
